/**
 * The per-user channel: one socket carrying everything that happens *to* a
 * player, across every domain.
 *
 * Before this existed each screen invented its own poll — activity every 15s,
 * queue status every 750ms, the ready room every 500ms, invites every 5s in
 * three separate places. They were all asking the same question ("has anything
 * changed for me?") of a server that had no way to answer without being asked.
 *
 * Two rules make the rest of the design fall out:
 *
 * 1. An event is only ever a *copy of a committed row*. It is written to
 *    `user_events` in the same D1 batch as the fact it describes, and the
 *    socket carries it afterwards. Delivery failure costs latency, never data.
 * 2. Consequently `/channel/sync?after=<seq>` returns byte-identical envelopes
 *    to the ones the socket sends. A client cannot tell live from recovered,
 *    so deduping by `id` is all it has to do.
 */

export const CHANNEL_PROTOCOL_VERSION = 1;
export const CHANNEL_SUBPROTOCOL = "guandan.channel.v1";

/**
 * Every kind of thing that can reach a player.
 *
 * A closed union rather than a free string: the client switches exhaustively
 * over it, so adding a case here is what forces the reducer to handle it.
 */
export const CHANNEL_EVENT_TYPES = [
  /** Someone invited me to a party or a lobby. */
  "invite.created",
  /** An invite I sent or received left `pending` — accepted, declined, cancelled, expired. */
  "invite.resolved",
  "friend.followed",
  "friend.unfollowed",
  /** A player I can see came online or went offline. */
  "presence.changed",
  /** My single recoverable activity changed (idle / queue / lobby / party / match). */
  "activity.changed",
  /** Matchmaking ticket progress while I am searching. */
  "queue.updated",
  /** Seats, readiness or membership changed in a lobby I am in. */
  "lobby.updated",
  /** A table is dealt and waiting for me. */
  "room.ready",
  /** Coins, XP or a level-up landed. */
  "reward.granted",
  /** Something a player I follow did, for the feed. */
  "feed.item",
] as const;
export type ChannelEventType = (typeof CHANNEL_EVENT_TYPES)[number];

const eventTypeSet = new Set<string>(CHANNEL_EVENT_TYPES);
export function isChannelEventType(value: unknown): value is ChannelEventType {
  return typeof value === "string" && eventTypeSet.has(value);
}

/**
 * One event on the wire.
 *
 * `entityId` + `entityVersion` are what stop a reordered delivery from undoing
 * newer state: the client applies an envelope only when it carries a version
 * strictly newer than the one it already holds for that entity. A push that
 * loses a race with a `/sync` response is therefore discarded rather than
 * rewinding an invite from `accepted` back to `pending`.
 */
export interface ChannelEnvelope<T = unknown> {
  readonly v: typeof CHANNEL_PROTOCOL_VERSION;
  /** Unique per event. The client's dedupe key. */
  readonly id: string;
  /** Monotonic per user. The resume cursor; also the total order. */
  readonly seq: number;
  readonly type: ChannelEventType;
  readonly entityType: string;
  readonly entityId: string;
  readonly entityVersion: number;
  /** Server time the fact was committed. */
  readonly at: number;
  readonly payload: T;
}

/** What the socket sends. Deliberately tiny — the socket is a delivery hint. */
export type ChannelServerMessage =
  /**
   * Sent once on connect, before any event.
   *
   * Carries the server's newest `seq` so a client that reconnects into a gap
   * knows immediately whether it needs to sync, rather than discovering it
   * only when an out-of-order event arrives.
   */
  | { kind: "ready"; protocol: number; serverTime: number; seq: number }
  | { kind: "events"; events: readonly ChannelEnvelope[] };

/**
 * What the client may send.
 *
 * Almost nothing, on purpose. Commands go over HTTP where they get status
 * codes, idempotency keys and retries; the socket is one-directional in
 * practice. Liveness is handled by the Durable Object's auto-response pair, so
 * a keepalive never wakes a hibernating object and never reaches this type.
 */
export type ChannelClientMessage = { kind: "resume"; after: number };

export function parseChannelClientMessage(value: unknown): ChannelClientMessage | null {
  if (typeof value !== "object" || value === null) return null;
  const message = value as Record<string, unknown>;
  if (message.kind !== "resume") return null;
  return typeof message.after === "number" && Number.isSafeInteger(message.after) && message.after >= 0
    ? { kind: "resume", after: message.after }
    : null;
}

/**
 * The keepalive pair registered with `setWebSocketAutoResponse`.
 *
 * The runtime answers these without ever invoking `webSocketMessage`, so a
 * hibernating channel stays hibernated. This is why the client must not reuse
 * the game socket's application-level ping: that one is a real message, and at
 * one every ten seconds it would wake each user's object six times a minute.
 */
export const CHANNEL_PING = "p";
export const CHANNEL_PONG = "o";

// --- payloads ----------------------------------------------------------------

export type InvitePresentation = {
  readonly id: string;
  readonly kind: "duo" | "gold" | "rematch" | "spectate";
  readonly state: "pending" | "accepting" | "accepted" | "declined" | "cancelled" | "expired";
  readonly senderId: string;
  readonly senderName: string;
  readonly recipientId: string;
  readonly destinationType: "party" | "lobby";
  readonly destinationCode: string;
  readonly createdAt: number;
  readonly expiresAt: number;
  readonly version: number;
};

export type PresencePayload = { readonly userId: string; readonly online: boolean };
export type FollowPayload = {
  readonly userId: string;
  readonly friends: boolean;
  readonly followers?: number;
  readonly friendsCount?: number;
};
export type RewardPayload = { readonly coins?: number; readonly xp?: number; readonly level?: number; readonly reason: string };
